import React, { useEffect, useRef, useState } from 'react';
import { scaleLinear } from 'd3-scale';
import { select, axisLeft } from 'd3';
import './depth-track.scss';

export default function StratigraphicSurface(props: any) {
  const svgRef: any = useRef();

  const { startDepth, 
          endDepth,
          depthUnit, 
          availableHeight,
          minHeaderHeight,
          trackTop, 
          headerBottomPadding,
          topMargin,
          leftStratigraphyLexiconStartAndEnds,
          rightStratigraphyLexiconStartAndEnds,
          correlations} = props;

  const parentWidth = 50;

  const margins = {
    top: minHeaderHeight + topMargin + 2,
    right: 0,
    bottom: headerBottomPadding,
    left: 0,
  };

  const width = parentWidth - margins.left - margins.right;   
  //const height = windowHeight - (minHeaderHeight + 128) - margins.top - margins.bottom;  
  
  const trackHeight = availableHeight - minHeaderHeight - topMargin - headerBottomPadding - 1;
  const svgHeight = availableHeight - 5; //trackHeight + headerBottomPadding - margins.top;
  const totalWidth = width + margins.left + margins.right;

  const getYRange = () => {
    if (endDepth && endDepth > startDepth) {
      return [startDepth, endDepth];
    }

    return [startDepth, endDepth];
  };

const renderChart = () => {
    const svg = select(svgRef.current);
    const yScale = scaleLinear()
    .domain(getYRange())
    .range([0, trackHeight]);

    svg
    .select("line").remove();    

    // If we have pre-computed correlations from the backend, use those
    if (correlations && Array.isArray(correlations) && correlations.length > 0) {
      correlations.forEach((correlation: any) => {
        svg
          .append("line")
          .attr("x1", 0) // Start at the left side
          .attr("y1", yScale(correlation.leftDepth)) // Vertical position on the left side
          .attr("x2", width) // End at the right side
          .attr("y2", yScale(correlation.rightDepth)) // Vertical position on the right side
          .attr("stroke", correlation.color) // Line color
          .attr("stroke-width", 2)
          .attr('clip-path', 'url(#clip)'); // Line thickness
      });
      return;
    }

    // Fallback to manual matching if no correlations provided
    // Track which right offsets have already been matched
    const matchedLeftOffsets = new Set();
    
    // Track drawn lines to check for crossings
    const drawnLines: Array<{leftY: number, rightY: number}> = [];
    const allowLineCrossing = true;
    
    // Helper function to normalize color by removing 'ff' opacity if present
    const normalizeColor = (color: string) => {
      if (!color) return color;
      const normalized = color.toLowerCase();
      // If color ends with 'ff' (full opacity), remove it for comparison
      if (normalized.length === 9 && normalized.endsWith('ff')) {
        return normalized.substring(0, 7);
      }
      return normalized;
    };
    
    // Helper function to compare colors ignoring 'ff' opacity
    const colorsMatch = (color1: string, color2: string) => {
      return normalizeColor(color1) === normalizeColor(color2);
    };
    
    // Helper function to check if two lines cross
    const linesCross = (line1: {leftY: number, rightY: number}, line2: {leftY: number, rightY: number}) => {
      // Lines cross if they have opposite orientations
      // Line 1 goes from line1.leftY to line1.rightY
      // Line 2 goes from line2.leftY to line2.rightY
      // They cross if one goes up while the other goes down relative to each other
      return (line1.leftY < line2.leftY && line1.rightY > line2.rightY) ||
             (line1.leftY > line2.leftY && line1.rightY < line2.rightY);
    };

    // Helper function to check if a proposed line would cross any already drawn lines
    const wouldCrossExistingLines = (leftOffset: any, rightOffset: any) => {
      if (!allowLineCrossing) {
        const proposedLine = {
          leftY: yScale(leftOffset.startDepth),
          rightY: yScale(rightOffset.startDepth)
        };
        
        for (const drawnLine of drawnLines) {
          if (linesCross(proposedLine, drawnLine)) {
            return true; // Would cross
          }
        }
      }
      return false; // Would not cross (or crossing is allowed)
    };

    // using d3 draw a line from the leftOffset to the rightOffset
     leftStratigraphyLexiconStartAndEnds.forEach((leftOffset: any) => {     
        if (matchedLeftOffsets.has(normalizeColor(leftOffset.color))) {
            return;
        }
        
        // Priority 1: Try to find an exact ID match first
        let rightOffset = rightStratigraphyLexiconStartAndEnds.find((rightOffset: any) => {
          const isDirectMatch = rightOffset.stratigraphyLexiconId === leftOffset.stratigraphyLexiconId;
          return isDirectMatch && !wouldCrossExistingLines(leftOffset, rightOffset);
        });
        
        // Priority 2: If no ID match, try color match
        if (!rightOffset) {
          rightOffset = rightStratigraphyLexiconStartAndEnds.find((rightOffset: any) => {
            const isColorMatch = colorsMatch(rightOffset.color, leftOffset.color);
            return isColorMatch && !wouldCrossExistingLines(leftOffset, rightOffset);
          });
        }
        
        // Priority 3: If no ID or color match, try alternative ID match
        if (!rightOffset) {
          rightOffset = rightStratigraphyLexiconStartAndEnds.find((rightOffset: any) => {
            const isAlternativeMatch = leftOffset.alternativeIds && 
                                        Array.isArray(leftOffset.alternativeIds) && 
                                        leftOffset.alternativeIds.includes(rightOffset.stratigraphyLexiconId);
            return isAlternativeMatch && !wouldCrossExistingLines(leftOffset, rightOffset);
          });
        }

        if (!rightOffset) {
          return; // Skip if no matching right offset found
        }

        // Mark this left offset as matched (using normalized color)
        matchedLeftOffsets.add(normalizeColor(leftOffset.color));
        
        // Record this line as drawn
        drawnLines.push({
          leftY: yScale(leftOffset.startDepth),
          rightY: yScale(rightOffset.startDepth)
        });

        // Draw the line from leftOffset to rightOffset
        svg
          .append("line")
          .attr("x1", 0) // Start at the left side
          .attr("y1", yScale(leftOffset.startDepth)) // Vertical position on the left side
          .attr("x2", width) // End at the right side
          .attr("y2", yScale(rightOffset.startDepth)) // Vertical position on the right side
          .attr("stroke", leftOffset.color) // Line color
          .attr("stroke-width", 2)
          .attr('clip-path', 'url(#clip)'); // Line thickness

      // svg
      //   .append("line")
      //   .attr("x1", 0) // Start at the left side
      //   .attr("y1", yScale(leftOffset.endDepth)) // Vertical position on the left side
      //   .attr("x2", width) // End at the right side
      //   .attr("y2", yScale(rightOffset.endDepth)) // Vertical position on the right side
      //   .attr("stroke", "black") // Line color
      //   .attr("stroke-width", 1); // Line thickness

      //   const points = [
      //     [0, yScale(leftOffset.startDepth)], // Top-left
      //     [width, yScale(rightOffset.startDepth)], // Top-right
      //     [width, yScale(rightOffset.endDepth)], // Bottom-right
      //     [0, yScale(leftOffset.endDepth)], // Bottom-left
      // ];

      // svg
      //       .append("polygon")
      //       .attr("points", points.map(point => point.join(",")).join(" ")) // Convert points to "x,y x,y ..." format
      //       .attr("fill", leftOffset.color) // Fill color with transparency
      //       .attr("stroke", "none") // Border color
      //       .attr("stroke-width", 0); // Border thickness
    });      
}

const clearChart = () => {
  const svg = select(svgRef.current);
  
  // using d3 draw a line from the leftOffset to the rightOffset  
  svg.selectAll("*").remove(); 
  //svg.select("line").remove(); 
  //svg.select("polygon").remove();
}

  useEffect(() => {    
    if (leftStratigraphyLexiconStartAndEnds && rightStratigraphyLexiconStartAndEnds) {
      clearChart();
      renderChart();
    } else {
      clearChart();
    }
  }, [ availableHeight, 
      startDepth, 
      endDepth, 
      depthUnit, 
      minHeaderHeight,
      trackTop,
      leftStratigraphyLexiconStartAndEnds,
      rightStratigraphyLexiconStartAndEnds,
      correlations]);

  return (
    <div className="depth-track">     
        <div className={`svg-chartdepthtrack`} >
            <svg width={totalWidth}
              height={svgHeight}>      
              <defs>
                <clipPath id="clip">
                  <rect className="clip-rect" x={0} y={0} width={1000} height={2000} />
                </clipPath>           
              </defs>
              <g ref={svgRef} transform={`translate(${margins.left}, ${margins.top})`}>
                <g className="unit" >
                    <text className="unit-text"></text>
                </g>
                <g className="y-axis" />
              </g>
            </svg>          
        </div>     
    </div>
  );  
}
